import { Link, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { SignOut, Globe, User as UserIcon, ShieldCheck, EnvelopeSimple } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { Toaster } from "sonner";

export function TopNav({ variant = "app" }) {
    const { lang, setLang, user, logout, t } = useApp();
    const nav = useNavigate();
    const loc = useLocation();

    return (
        <header className="glass sticky top-0 z-40">
            <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
                <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2.5 group" data-testid="brand-link">
                    <Logo size={34} />
                    <span className="font-heading font-bold text-lg tracking-tight text-shield-ink">
                        Letzter<span className="text-shield-terracotta">·</span>Schlüssel
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-shield-muted">
                    {variant === "landing" && (
                        <>
                            <a href="#how" className="hover:text-shield-forest transition-colors" data-testid="nav-how">{t("nav.howItWorks")}</a>
                            <a href="#security" className="hover:text-shield-forest transition-colors" data-testid="nav-security">{t("nav.security")}</a>
                            <a href="#price" className="hover:text-shield-forest transition-colors" data-testid="nav-price">{t("nav.price")}</a>
                            <Link to="/legal" className="hover:text-shield-forest transition-colors" data-testid="nav-legal">{t("nav.legal")}</Link>
                        </>
                    )}
                </nav>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setLang(lang === "de" ? "en" : "de")}
                        className="inline-flex items-center gap-1.5 px-3 h-10 pill text-sm font-semibold text-shield-forest hover:bg-white/60 transition-colors"
                        data-testid="lang-toggle"
                        aria-label="Switch language"
                    >
                        <Globe size={16} weight="bold" aria-hidden="true" />
                        {lang.toUpperCase()}
                    </button>

                    {user ? (
                        <>
                            {loc.pathname !== "/dashboard" && (
                                <button
                                    onClick={() => nav("/dashboard")}
                                    className="hidden sm:inline-flex h-10 px-4 pill text-sm font-semibold text-shield-forest hover:bg-white/60"
                                    data-testid="nav-dashboard"
                                >
                                    {t("nav.dashboard")}
                                </button>
                            )}
                            <button
                                onClick={async () => { await logout(); nav("/"); }}
                                className="inline-flex items-center gap-1.5 h-10 px-4 pill text-sm font-semibold text-shield-ink bg-white border border-shield-line hover:bg-shield-sage transition-colors"
                                data-testid="nav-logout"
                            >
                                <SignOut size={16} weight="bold" aria-hidden="true" />
                                {t("nav.logout")}
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={() => nav("/login")}
                                className="hidden sm:inline-flex h-10 px-4 pill text-sm font-semibold text-shield-forest hover:bg-white/60"
                                data-testid="nav-login"
                            >
                                {t("nav.login")}
                            </button>
                            <button
                                onClick={() => nav("/register")}
                                className="inline-flex items-center gap-1.5 h-10 px-5 pill text-sm font-semibold bg-shield-forest text-white hover:bg-shield-forestDeep transition-colors"
                                data-testid="nav-cta"
                            >
                                <UserIcon size={16} weight="bold" aria-hidden="true" />
                                {t("nav.getStarted")}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
}

export function GlobalFooter() {
    const { t } = useApp();
    return (
        <footer className="border-t border-shield-line py-10 mt-16 bg-white/60">
            <div className="mx-auto max-w-7xl px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex items-center gap-2 text-shield-muted text-sm">
                    <ShieldCheck size={18} weight="duotone" aria-hidden="true" />
                    {t("landing.footerTag")}
                </div>
                <nav
                    aria-label="Rechtliches"
                    className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-semibold"
                >
                    <Link
                        to="/legal?tab=impressum"
                        className="text-shield-forest hover:text-shield-forestDeep hover:underline underline-offset-4 transition-colors"
                        data-testid="footer-impressum"
                    >
                        {t("legal.tabs.impressum")}
                    </Link>
                    <Link
                        to="/legal?tab=datenschutz"
                        className="text-shield-forest hover:text-shield-forestDeep hover:underline underline-offset-4 transition-colors"
                        data-testid="footer-datenschutz"
                    >
                        {t("legal.tabs.datenschutz")}
                    </Link>
                    <Link
                        to="/legal?tab=agb"
                        className="text-shield-forest hover:text-shield-forestDeep hover:underline underline-offset-4 transition-colors"
                        data-testid="footer-agb"
                    >
                        {t("legal.tabs.agb")}
                    </Link>
                    <span className="hidden md:inline-flex items-center gap-1 text-shield-muted font-normal">
                        <EnvelopeSimple size={14} aria-hidden="true" /> letzter-schluessel@gmx.net
                    </span>
                </nav>
            </div>
        </footer>
    );
}

export function AppShell({ children, variant = "app" }) {
    return (
        <div className="min-h-screen flex flex-col">
            <TopNav variant={variant} />
            <main className="flex-1">{children}</main>
            <GlobalFooter />
            <Toaster position="top-center" richColors closeButton />
        </div>
    );
}
